import React, { useEffect, useState, ReactNode } from 'react';
import { Box, FormControl, IconButton } from 'vendor/material';
import { Checkbox } from '@tackle-io/platform-ui';
import { Information, Close } from 'mdi-material-ui';
import { useStyles } from './PersistDismissableBanner.styles';

export const PERSIST_DISMISS_BANNER_TEST_ID = 'persist-dismissable-banner';

export const BANNER_STORAGE_KEY_PREFIX = 'banner_dismissed_';

interface PersistDismissableBannerProps {
  /** Unique identifier for the banner (used for local storage) */
  id: string;
  /** Content to display in the banner (can be string or JSX) */
  content: ReactNode | string;
  /** Whether to show the permanent dismiss checkbox */
  showPermanentDismiss?: boolean;
  /** User ID for the current user (required only for permanent dismissal) */
  userId?: string;
  /** Vendor ID associated with this banner (required only for permanent dismissal) */
  vendorId?: string;
  onClose: () => void;
}

export default function PersistDismissableBanner({
  id,
  content,
  onClose,
  showPermanentDismiss = true,
  userId,
  vendorId,
}: PersistDismissableBannerProps) {
  const classes = useStyles();
  const [permanentDismissChecked, setPermanentDismissChecked] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const storageKey = `${BANNER_STORAGE_KEY_PREFIX}${id}${
      userId ? `_${userId}` : ''
    }${vendorId ? `_${vendorId}` : ''}`;
    const isDismissed = localStorage.getItem(storageKey) === 'true';
    if (isDismissed) {
      setIsVisible(false);
      onClose();
    }
  }, [id, userId, vendorId, onClose]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPermanentDismissChecked(event.target.checked);
  };

  const handleDismiss = () => {
    if (permanentDismissChecked) {
      const storageKey = `banner_dismissed_${id}${userId ? `_${userId}` : ''}${
        vendorId ? `_${vendorId}` : ''
      }`;
      localStorage.setItem(storageKey, 'true');
    }
    setIsVisible(false);
    onClose();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      data-testid={PERSIST_DISMISS_BANNER_TEST_ID}
      className={classes.bannerContainer}
    >
      <Box className={classes.iconContentWrapper}>
        <Information
          data-testid="banner-info-icon"
          className={classes.infoIcon}
        />
        <Box className={classes.content}>{content}</Box>
      </Box>

      {showPermanentDismiss && (
        <Box className={classes.checkboxContainer}>
          <FormControl>
            <Checkbox
              checked={permanentDismissChecked}
              onChange={handleCheckboxChange}
              size="small"
              className={classes.checkbox}
              label="Do not show again"
              data-testid="permanent-dismiss-checkbox"
              inputProps={{
                'aria-label': 'Do not show again',
                'aria-describedby': `${id}-checkbox-description`,
                role: 'checkbox',
              }}
            />
          </FormControl>
        </Box>
      )}

      <Box className={classes.closeButtonContainer}>
        <IconButton
          size="small"
          aria-label="close"
          onClick={handleDismiss}
          className={classes.closeButton}
        >
          <Close
            data-testid="banner-close-icon"
            fontSize="small"
            className={classes.closeIcon}
          />
        </IconButton>
      </Box>
    </Box>
  );
}
